'use client'
import * as React from 'react'

interface UseIndexDbValueOptions {
  dbName: string
  dbVersion: number
  storeName: string
  key: string
  initialValue?: unknown
  enabled?: boolean
}

function openDatabase(dbName: string, dbVersion: number, storeName: string): Promise<IDBDatabase> {
  // oxlint-disable-next-line promise/avoid-new
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName, dbVersion)

    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result)

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(storeName)) {
        db.createObjectStore(storeName)
      }
    }
  })
}

export async function getIndexDbValue<T>(
  dbName: string,
  dbVersion: number,
  storeName: string,
  key: string,
): Promise<T | null> {
  try {
    const db = await openDatabase(dbName, dbVersion, storeName)
    // oxlint-disable-next-line promise/avoid-new
    return new Promise<T | null>((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readonly')
      const store = transaction.objectStore(storeName)
      const request = store.get(key)
      request.onsuccess = () => {
        const result = request.result
        // oxlint-disable-next-line no-negated-condition
        resolve(result !== undefined ? (result as T) : null)
      }
      request.onerror = () => reject(request.error)
    })
  } catch {
    return null
  }
}

export async function setIndexDbValue<T>(
  dbName: string,
  dbVersion: number,
  storeName: string,
  key: string,
  value: T | null,
): Promise<void> {
  try {
    const db = await openDatabase(dbName, dbVersion, storeName)
    // oxlint-disable-next-line promise/avoid-new
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite')
      const store = transaction.objectStore(storeName)
      if (value === null || value === undefined) {
        const request = store.delete(key)
        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
      } else {
        const request = store.put(value, key)
        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
      }
    })
  } catch {
    // Silently fail
  }
}

export function useIndexDbValue<T>(
  options: UseIndexDbValueOptions,
): [T | null, React.Dispatch<React.SetStateAction<T | null>>, boolean] {
  const { dbName, dbVersion, storeName, key, initialValue, enabled = true } = options

  const [value, setValue] = React.useState<T | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)

  React.useEffect(() => {
    if (!enabled) {
      setIsLoading(false)
      return
    }

    let cancelled = false

    const loadValue = async () => {
      setIsLoading(true)
      const stored = await getIndexDbValue<T>(dbName, dbVersion, storeName, key)
      if (!cancelled) {
        setValue(stored ?? (initialValue as T | null) ?? null)
        setIsLoading(false)
      }
    }

    void loadValue()

    return () => {
      cancelled = true
    }
  }, [dbName, dbVersion, storeName, key, initialValue, enabled])

  const setState = React.useCallback(
    async (v: React.SetStateAction<T | null>) => {
      const nextValue = typeof v === 'function' ? (v as (prev: T | null) => T | null)(value) : v
      setValue(nextValue)
      await setIndexDbValue(dbName, dbVersion, storeName, key, nextValue)
    },
    [dbName, dbVersion, storeName, key, value],
  )

  return [value, setState, isLoading]
}
